import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { MetaLoaderListDTO, RagLoader } from '../../models/LLM';
import { Llm } from '../../api/Llm';
import { useToasts } from '../../contexts/toasts';
import { TailSpin } from 'react-loader-spinner';
import { Button, Form } from 'react-bootstrap';
import { cloneDeep, set } from 'lodash';
import LoaderClassDropdown from './LoaderClassDropdown';
import { Modal, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { accessLevelOptions } from '../../utils';

type Props = {
  open: boolean;
  loader: RagLoader;
  onClose?: (() => any);
  onSubmit?: ((values: any) => void);
};

export default function EditLoaderModal({ loader, open, onClose, onSubmit }: Props) {
  const [metaLoaders, setMetaLoaders] = useState<MetaLoaderListDTO[]>();
  const [validated, setValidated] = useState(false);
  const [editingLoader, setEditingLoader] = useState<RagLoader>(loader);
  const selectedMetaLoader = useMemo(() => metaLoaders?.find(m => m.name === editingLoader.type), [metaLoaders, editingLoader]);

  const { showToast } = useToasts();

  const initMetaLoaders = useCallback(() => {
    Llm.getLoadersDocumentation()
      .then(
        ({ data }) => setMetaLoaders(data),
        (err) => {
          setMetaLoaders([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('meta-loaders-error', message, `Loaders Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(() => initMetaLoaders(), [initMetaLoaders]);
  useEffect(() => setEditingLoader(loader), [loader]);
  useEffect(() => setValidated(false), [open]);

  const getLoaderDocumentation = useCallback(() => {
    Llm.getLoaderDocumentation(selectedMetaLoader!.name)
      .then(
        ({ data }) => setEditingLoader(l => ({ ...l, config: data.config })),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('meta-loader-error', message, `Loader Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [selectedMetaLoader, showToast]);

  useEffect(() => {
    if (!editingLoader.config && editingLoader.type && selectedMetaLoader) getLoaderDocumentation();
  }, [editingLoader, selectedMetaLoader, getLoaderDocumentation]);

  const handleSubmit = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    setValidated(true);
    evt.stopPropagation();
    evt.preventDefault();
    const form = evt.currentTarget;

    if (
      form.checkValidity() === true && editingLoader.accessLevel &&
      editingLoader.type && editingLoader.config
    ) onSubmit?.(editingLoader);
  }, [editingLoader, onSubmit]);

  const handleClassLoaderChange = useCallback((type: string) => setEditingLoader(l => ({ ...l, type, config: undefined })), []);

  const handleFieldChange = useCallback((path: string, pure?: boolean, checkbox?: boolean) => (evt: any) => {
    const value = checkbox
      ? evt.target?.checked
      : pure ? evt : evt.target?.value;

    setEditingLoader(curr => {
      const newLoader = cloneDeep(curr!);
      set(newLoader, path, value);
      return newLoader;
    });
  }, []);

  const renderMetaLoaderDetail = useCallback(() => {
    if (!editingLoader.config && editingLoader.type) return (
      <div className="d-flex justify-content-center py-5">
        <TailSpin
          height={100}
          width={100}
          color="#677ab1"
          radius="1"
        />
      </div>
    )

    return editingLoader.config?.map((prop, index) => (
      <Form.Group className="mb-3" key={index}>
        <Form.Label>{prop.name} {prop.required && '*'}</Form.Label>
        {prop.type === 'boolean'
          ? <Form.Check checked={prop.value as boolean} onChange={handleFieldChange(`config.[${index}].value`, false, true)} />
          : prop.type === 'array'
            ? <Select className="w-100" mode="tags" placeholder={prop.default ? `${prop.default}` : prop.type} value={prop.value as string[] || []} onChange={handleFieldChange(`config.[${index}].value`, true)} />
            : <Form.Control size="sm" placeholder={prop.default ? `${prop.default}` : prop.type} value={prop.value as string || ""} onChange={handleFieldChange(`config.[${index}].value`)} required={prop.required} />
        }
        <Form.Control.Feedback type="invalid">{prop.name} is required.</Form.Control.Feedback>
        <Form.Text>{prop.description}</Form.Text>
      </Form.Group>
    ))
  }, [editingLoader, handleFieldChange]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={600}
      footer={null}
    >
      <h6>{loader?.id ? 'Edit' : 'Create'} Loader</h6>

      <br />

      <Form validated={validated} onSubmit={handleSubmit} noValidate>
        <Form.Group className="mb-3" controlId="loader-name">
          <Form.Label>Name</Form.Label>
          <Form.Control placeholder="Loader Name" size="sm" value={editingLoader.name} onChange={handleFieldChange('name')} required />
          <Form.Control.Feedback type="invalid">Loader name is required.</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="loader-description">
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" rows={3} name="description" placeholder="Loader Description" size="sm" value={editingLoader.description} onChange={handleFieldChange('description')} />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Privacy Level</Form.Label>
          <Select
            placeholder="Select privacy level"
            value={editingLoader.accessLevel}
            allowClear
            onChange={handleFieldChange('accessLevel', true)}
            className="w-100"
          >
            {accessLevelOptions.map((option, index) => (
              <Select.Option key={index} value={option.value} label={option.label}>
                <FontAwesomeIcon icon={option.icon} fixedWidth />
                <span className="ms-2">{option.label}</span>
              </Select.Option>
            ))}
          </Select>
          {validated && !editingLoader.accessLevel && <Form.Control.Feedback className="d-block" type="invalid">Privacy Level is required.</Form.Control.Feedback>}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Tags</Form.Label>
          <Select mode="tags" className="w-100" placeholder="Loader Tags" value={editingLoader?.tags} onChange={handleFieldChange('tags', true)} />
        </Form.Group>

        <Form.Group className="mb-3" controlId="loader-class">
          <Form.Label className="mb-0">Loader Class</Form.Label>
          <LoaderClassDropdown
            value={editingLoader.type}
            loaders={metaLoaders}
            onChange={handleClassLoaderChange}
          />
          {validated && !editingLoader.type && <Form.Control.Feedback className="d-block" type="invalid">Loader Class is required.</Form.Control.Feedback>}
        </Form.Group>

        {renderMetaLoaderDetail()}

        <div className="d-flex justify-content-between mt-3">
          <Button type="button" variant="light" className="border" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="success" size="sm">
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save</span>
          </Button>
        </div>
      </Form>
    </Modal>
  )
}